import { FileFormat } from '@/types/file-format';
import { VideoResolution as VideoResolutionPreference } from '@/types/video-resolution';

import { getRules, holdsPermission } from './internal/rules';
import { computeStockVideoEffectiveTier } from './internal/stockVideoTier';
import { RESOLUTION_ORDER } from './types';

import type {
  AssetRef,
  AudioFormat,
  DownloadDecision,
  GatewayInput,
  GatewaySnapshot,
  RequestedFormat,
  UpsellTargetPlan,
  VideoResolution,
} from './types';

/**
 * Pure decision function for the Download Gateway.
 *
 * No React, no hooks, no atoms — takes plain data and returns a plain
 * `DownloadDecision`. The hook wrapper composes the snapshot and delegates
 * here; tests call this directly with fixture snapshots, which is why every
 * branch in here has to be reachable from a hand-constructed snapshot.
 *
 * Asset kinds without a `RULES` row resolve to `pending` so callers can
 * detect and fall back to a non-gateway path.
 */
export const evaluateDownload = (
  input: GatewayInput,
  snapshot: GatewaySnapshot
): DownloadDecision => {
  if (!snapshot.isSettingsLoaded) {
    return { action: 'pending' };
  }

  const { asset, requested } = input;

  // --- Cross-cutting prelude -------------------------------------------------

  if (!snapshot.user.isAuthenticated) {
    return {
      action: 'navigate',
      to: snapshot.user.isReturningUser ? 'login' : 'sign-up',
      welcomeMessageType: asset.isPremium ? 'premium-download' : 'download',
    };
  }

  if (snapshot.overduePayment) {
    return { action: 'navigate', to: 'overdue-payment' };
  }

  if (snapshot.subscriptionPaused) {
    return { action: 'navigate', to: 'pause-warning' };
  }

  // --- Asset-kind-specific decision -----------------------------------------

  if (asset.kind === 'music' || asset.kind === 'sfx') {
    return evaluateAudio(asset, requested, snapshot);
  }

  if (asset.kind === 'mg' || asset.kind === 'lut') {
    return evaluateMotion(asset, snapshot);
  }

  if (asset.kind === 'stock-video') {
    return evaluateStockVideo(asset, requested, snapshot);
  }

  return { action: 'pending' };
};

const userHasPayingRole = (snapshot: GatewaySnapshot): boolean =>
  snapshot.roles.has('Essentials') ||
  snapshot.roles.has('Creator') ||
  snapshot.roles.has('Pro') ||
  snapshot.roles.has('Premium') ||
  snapshot.roles.has('Business');

const evaluateAudio = (
  asset: AssetRef,
  requested: RequestedFormat,
  snapshot: GatewaySnapshot
): DownloadDecision => {
  // Caller in `evaluateDownload` already gates on kind; the runtime guard
  // exists so TypeScript narrows `asset.kind` within this function (the
  // `AssetRef` union arm covers all of music/sfx/mg/lut and can't be split
  // with `Extract`).
  if (asset.kind !== 'music' && asset.kind !== 'sfx') {
    return { action: 'pending' };
  }
  const rules = getRules(asset.kind);
  if (!rules) {
    return { action: 'pending' };
  }

  const hasPremiumPermission = holdsPermission(
    snapshot.permissions,
    rules.premiumPermission
  );
  const hasUnlimited = holdsPermission(
    snapshot.permissions,
    rules.unlimitedPermission
  );
  const canDownloadWav = snapshot.permissions.has('download_format_wav');

  // Premium asset: must hold the per-kind premium permission.
  if (asset.isPremium && !hasPremiumPermission) {
    // `hasNoDownloadsLeft` describes the credit pool only — unlimited users
    // with 0 credits still aren't "out of downloads", they just lack the
    // premium grant.
    return upsell(
      'premium-asset',
      rules.upsellTarget,
      !hasUnlimited && snapshot.credits <= 0
    );
  }

  // Free user + free asset: gate on email verification before anything else.
  // Paying tiers (Essentials/Creator/Pro) skip this — matching legacy behaviour.
  if (
    !(
      asset.isPremium ||
      userHasPayingRole(snapshot) ||
      snapshot.user.isEmailVerified
    )
  ) {
    return { action: 'navigate', to: 'verify-email' };
  }

  // Credit reconciliation: if the user doesn't have unlimited downloads and
  // is out of credits, upsell.
  if (!hasUnlimited && snapshot.credits <= 0) {
    return upsell('no-credits', rules.upsellTarget, true);
  }

  // Credit acknowledgement: users consuming credits need to acknowledge
  // credit usage before the download kicks off, unless they already did.
  const requiresConfirmation =
    !hasUnlimited && snapshot.credits > 0 && !snapshot.creditUsageAcknowledged;

  // Requested format wins if explicitly provided.
  if (requested?.type === 'audio') {
    if (requested.format === 'wav' && !canDownloadWav) {
      // Same `hasNoDownloadsLeft` semantics as the premium-asset upsell:
      // unlimited users gated only on WAV permission still have downloads.
      return upsell(
        'format-locked',
        rules.upsellTarget,
        !hasUnlimited && snapshot.credits <= 0
      );
    }
    return {
      action: 'download',
      format: { type: 'audio', format: requested.format },
      requiresConfirmation,
    };
  }

  // No explicit request: offer the chooser when WAV is unlocked and the
  // user's stored preference is "ask every time". Otherwise resolve to the
  // user's default format (or mp3 when WAV is locked).
  if (canDownloadWav && snapshot.formatPreference === FileFormat.AskEveryTime) {
    return {
      action: 'ask-format',
      choice: { type: 'audio', options: ['mp3', 'wav'], wavLocked: false },
    };
  }

  const resolved: AudioFormat =
    canDownloadWav && snapshot.formatPreference === FileFormat.WAV
      ? 'wav'
      : 'mp3';

  return {
    action: 'download',
    format: { type: 'audio', format: resolved },
    requiresConfirmation,
  };
};

/**
 * Motion graphics + LUTs. The downloaded artefact is whatever the asset is
 * (`.zip` for MG, `.cube` for LUT) so the decision never asks the user to
 * pick a format — always returns `format: { type: 'file' }` when allowed.
 *
 * LUTs honour both the LUT-specific permissions and the broader MG ones
 * (the OR-fallback lives in RULES), which lets users grandfathered into
 * a single permission scope still download LUTs without the upsell.
 */
const evaluateMotion = (
  asset: AssetRef,
  snapshot: GatewaySnapshot
): DownloadDecision => {
  if (asset.kind !== 'mg' && asset.kind !== 'lut') {
    return { action: 'pending' };
  }
  const rules = getRules(asset.kind);
  if (!rules) {
    return { action: 'pending' };
  }

  const hasPremiumPermission = holdsPermission(
    snapshot.permissions,
    rules.premiumPermission
  );
  const hasUnlimited = holdsPermission(
    snapshot.permissions,
    rules.unlimitedPermission
  );

  // Premium asset gate. `hasNoDownloadsLeft` describes the credit pool only
  // — unlimited users with 0 credits still aren't "out of downloads", they
  // just lack the premium grant (matching `evaluateAudio`'s semantics).
  if (asset.isPremium && !hasPremiumPermission) {
    return upsell(
      'premium-asset',
      rules.upsellTarget,
      !hasUnlimited && snapshot.credits <= 0
    );
  }

  // Free user + free asset + unverified email — same prelude as audio.
  if (
    !(
      asset.isPremium ||
      userHasPayingRole(snapshot) ||
      snapshot.user.isEmailVerified
    )
  ) {
    return { action: 'navigate', to: 'verify-email' };
  }

  // Credit reconciliation.
  if (!hasUnlimited && snapshot.credits <= 0) {
    return upsell('no-credits', rules.upsellTarget, true);
  }

  // LUTs skip the confirmation screen for Creator/Pro because a .cube file
  // is a few kilobytes — the file-info screen is friction with no payoff.
  // Motion graphics keep the confirmation screen because the .zip is often
  // tens of MB and users want to see the file size before committing. This
  // is the only place in the gateway where a credit-consuming download
  // skips the credit-ack UI: the higher tiers have a generous credit pool
  // and the prompt would just be noise.
  const isCreatorOrPro =
    snapshot.roles.has('Creator') ||
    snapshot.roles.has('Pro') ||
    snapshot.roles.has('Premium') ||
    snapshot.roles.has('Business');

  const requiresConfirmation = !(asset.kind === 'lut' && isCreatorOrPro);

  return {
    action: 'download',
    format: { type: 'file' },
    requiresConfirmation,
  };
};

/**
 * Stock video. Two structural differences from audio/motion:
 *
 *   1. Permissions bundle into an elevated tier. The hook precomputes the
 *      bundle (via `computeStockVideoBundle`) and caches it on
 *      `asset.bundle`; the evaluator combines that with the user's plan
 *      role to derive the effective tier (via
 *      `computeStockVideoEffectiveTier`) and the per-resolution lock.
 *
 *   2. Format choice is per-resolution, not per-format. The chooser returns
 *      `ask-format` with `{ type: 'video'; options; lockedAbove }`. When the
 *      caller pre-requests a resolution above the user's cap, we upsell with
 *      `reason: 'resolution-locked'` and the resolution-appropriate target
 *      plan (4k → Pro; 1080p → Creator).
 */
const evaluateStockVideo = (
  asset: AssetRef,
  requested: RequestedFormat,
  snapshot: GatewaySnapshot
): DownloadDecision => {
  if (asset.kind !== 'stock-video') {
    return { action: 'pending' };
  }
  const rules = getRules('stock-video');
  if (!rules) {
    return { action: 'pending' };
  }

  const tier = computeStockVideoEffectiveTier(asset.bundle, snapshot.roles);
  const hasPremiumPermission = holdsPermission(
    snapshot.permissions,
    rules.premiumPermission
  );
  const hasUnlimited = holdsPermission(
    snapshot.permissions,
    rules.unlimitedPermission
  );

  // Pro/Creator tiers (whether by bundle or by plan role) hold premium and
  // unlimited grants by construction — they never hit premium-asset or
  // no-credits upsell. Only the tier-below set is gated.
  const tierBelowPremium =
    tier === 'free' || tier === 'essentials' || tier === 'unknown';

  if (tierBelowPremium) {
    if (asset.isPremium && !hasPremiumPermission) {
      // `hasNoDownloadsLeft` describes the credit pool only — a user with
      // an unlimited-stock-video grant (but no premium grant) still has
      // downloads; they're only gated on the premium permission.
      return upsell(
        'premium-asset',
        'Creator',
        !hasUnlimited && snapshot.credits <= 0
      );
    }
    if (!hasUnlimited && snapshot.credits <= 0) {
      return upsell('no-credits', 'Creator', true);
    }
  }

  const lockedAbove = lockedAboveForTier(tier);

  // Pre-requested resolution: download if within cap, upsell otherwise.
  if (requested?.type === 'video') {
    if (isResolutionLocked(requested.resolution, lockedAbove)) {
      return upsell(
        'resolution-locked',
        requested.resolution === '4k' ? 'Pro' : 'Creator',
        !hasUnlimited && snapshot.credits <= 0
      );
    }
    return {
      action: 'download',
      format: { type: 'video', resolution: requested.resolution },
      requiresConfirmation: false,
    };
  }

  // No pre-requested resolution: honour the saved account preference, and
  // only open the chooser when it's "Ask every time" (or unset) — DEV-6677.
  //
  // Only for uncapped users: "Ask every time" is the one preference the
  // account page hides below Pro, so a capped user's stored resolution isn't
  // a real opt-out — and their chooser doubles as the resolution upsell.
  const preferred =
    lockedAbove === null
      ? preferredResolution(snapshot.resolutionPreference)
      : undefined;

  if (preferred) {
    return {
      action: 'download',
      format: { type: 'video', resolution: preferred },
      requiresConfirmation: false,
    };
  }

  return {
    action: 'ask-format',
    choice: {
      type: 'video',
      options: ['720p', '1080p', '4k'],
      lockedAbove,
    },
  };
};

const lockedAboveForTier = (
  tier: ReturnType<typeof computeStockVideoEffectiveTier>
): '720p' | '1080p' | null => {
  if (tier === 'pro') {
    return null;
  }
  if (tier === 'creator') {
    return '1080p';
  }
  // essentials / free / unknown — 720p available, higher resolutions locked.
  return '720p';
};

const RESOLUTION_BY_PREFERENCE: Record<number, VideoResolution> = {
  [VideoResolutionPreference.p720]: '720p',
  [VideoResolutionPreference.p1080]: '1080p',
  [VideoResolutionPreference.p4K]: '4k',
};

/**
 * The resolution the user picked in account preferences, or `undefined` when
 * they chose "Ask every time" / haven't chosen at all.
 */
const preferredResolution = (
  preference: VideoResolutionPreference | undefined
): VideoResolution | undefined =>
  preference === undefined ? undefined : RESOLUTION_BY_PREFERENCE[preference];

const isResolutionLocked = (
  resolution: VideoResolution,
  lockedAbove: '720p' | '1080p' | null
): boolean => {
  if (lockedAbove === null) {
    return false;
  }
  const requestedIdx = RESOLUTION_ORDER.indexOf(resolution);
  const lockedAboveIdx = RESOLUTION_ORDER.indexOf(lockedAbove);
  return requestedIdx > lockedAboveIdx;
};

const upsell = (
  reason:
    | 'premium-asset'
    | 'no-credits'
    | 'format-locked'
    | 'resolution-locked',
  targetPlan: UpsellTargetPlan,
  hasNoDownloadsLeft: boolean
): DownloadDecision => ({
  action: 'upsell',
  reason,
  targetPlan,
  hasNoDownloadsLeft,
});
